import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft,
  Loader2,
  Paperclip,
  FileText,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import {
  useMarkRequestRead,
  useRequestAttachments,
  useRequestMessages,
  usePostMessage,
  useSetRequestStatus,
  useSupportRequest,
  useUploadAttachment,
} from '../../hooks/useSupportRequests';
import {
  STATUS_LABEL,
  TYPE_LABEL,
  type EllieArtifact,
  type RequestAttachment,
  type RequestMessage,
  type RequestStatus,
} from '../../services/supportRequest.service';
import { useUserConfig } from '../../hooks/useUserConfig';
import { StatusBadge, RelativeTime } from './requestUi';
import { MessageRow, AttachmentChip } from './MessageRow';
import { isUnread, unreadReplyCounts } from './unread';
import { useAppAuth } from '@/hooks/useAppAuth';
import { ThreadPanel } from './ThreadPanel';
import { ArtifactTakeover } from './ArtifactTakeover';
import { AttachArtifactDialog } from './AttachArtifactDialog';
import { ManageRequest } from './ManageRequest';

const STATUS_OPTIONS: RequestStatus[] = [
  'awaiting_pliable',
  'awaiting_customer',
  'in_progress',
  'done',
  'declined',
];

/**
 * How close to the bottom still counts as "following the conversation".
 *
 * A few pixels of slack matters: sub-pixel layout and the composer's own height
 * changes mean an exact comparison reads as "scrolled up" when the reader
 * hasn't touched anything.
 */
const PINNED_THRESHOLD_PX = 120;

/** The conversation column. Capped rather than fluid: full-bleed message rows on
 *  a wide monitor give lines nobody can track back to the start of. The composer
 *  reuses this so its edges line up with the messages above it — a `max-w` (not a
 *  fixed `w`) so both still shrink together below 700px. */
const CONTENT_WIDTH = 'w-full max-w-[700px] mx-auto';

/** Stable empty list, so the unread effect isn't re-run by a fresh [] each render. */
const EMPTY_MESSAGES: RequestMessage[] = [];

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: request, isLoading } = useSupportRequest(id ?? null);
  const { data: messages, isLoading: messagesLoading } = useRequestMessages(id ?? null);
  const { data: attachments } = useRequestAttachments(id ?? null);

  // Which artifact is open full-screen, and the message it was posted on — the
  // message is what the takeover's thread panel resolves a root from.
  const [openArtifact, setOpenArtifact] = useState<{
    artifactId: string;
    messageId: string;
  } | null>(null);
  // A thread opened from the conversation itself, shown in the right drawer.
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const { user: currentUser } = useAppAuth();

  // Opening the thread cancels any pending email for this user — the debounced
  // job re-checks the read cursor before it sends. It also owns the unread
  // baseline, which is why it needs the messages and the reader: posting a
  // reply retires the badges.
  const readBaseline = useMarkRequestRead(
    id ?? null,
    messages ?? EMPTY_MESSAGES,
    currentUser?.id,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading request
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3">
        <p className="text-sm text-muted-foreground">That request doesn't exist.</p>
        <Button variant="outline" onClick={() => navigate('/requests')}>
          Back to requests
        </Button>
      </div>
    );
  }

  return (
    <div
      className="w-full flex flex-col bg-background overflow-hidden"
      style={{ height: 'calc(100vh)' }}
    >
      <header className="px-6 py-4 page-header-surface flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <SidebarTrigger className="md:hidden h-5 w-5" />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 -ml-2 text-muted-foreground"
                onClick={() => navigate('/requests')}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Requests
              </Button>
            </div>
            <h1 className="text-xl font-semibold text-foreground truncate">
              {request.title}
            </h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <StatusBadge status={request.status} />
              <Badge variant="outline" className="h-5 px-1.5 text-[11px] font-normal">
                {TYPE_LABEL[request.request_type]}
              </Badge>
              {request.opened_by_ellie && (
                <Badge variant="outline" className="h-5 px-1.5 text-[11px] font-normal gap-1">
                  <Sparkles className="h-3 w-3" />
                  Opened via Ellie
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                Opened{' '}
                {request.created_by_label && (
                  <>
                    by{' '}
                    <span className="font-medium">{request.created_by_label}</span>{' '}
                  </>
                )}
                <RelativeTime iso={request.created_at} />
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <RequestControls requestId={request.id} status={request.status} />
            <ManageRequest request={request} />
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <Thread
            messages={messages ?? []}
            readBaseline={readBaseline}
            requestId={request.id}
            loading={messagesLoading}
            onOpenArtifact={(artifactId, messageId) =>
              setOpenArtifact({ artifactId, messageId })
            }
            onOpenThread={setOpenThreadId}
          />
          <Composer requestId={request.id} />
        </div>

        {/* One right-hand rail: the open thread takes precedence over the file
            list, since a thread is something you are actively doing. */}
        {openThreadId ? (
          <aside className="w-96 flex-shrink-0 border-l border-border bg-muted/20 min-h-0 flex flex-col">
            <div className="flex items-center justify-end px-2 pt-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setOpenThreadId(null)}
                aria-label="Close thread"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex-1 min-h-0">
              <ThreadPanel
                requestId={request.id}
                messageId={openThreadId}
                onOpenArtifact={(artifactId, messageId) =>
                  setOpenArtifact({ artifactId, messageId })
                }
              />
            </div>
          </aside>
        ) : (
          <AttachmentSidebar requestId={request.id} attachments={attachments ?? []} />
        )}
      </div>

      <ArtifactTakeover
        requestId={request.id}
        artifactId={openArtifact?.artifactId ?? null}
        messageId={openArtifact?.messageId ?? null}
        onClose={() => setOpenArtifact(null)}
        onOpenArtifact={(artifactId, messageId) =>
          setOpenArtifact({ artifactId, messageId })
        }
      />
    </div>
  );
}

function RequestControls({
  requestId,
  status,
}: {
  requestId: string;
  status: RequestStatus;
}) {
  const setStatus = useSetRequestStatus();
  const { data: userConfig } = useUserConfig();

  // Pliable-team only, and the backend enforces it with `staff_only=True`
  // rather than a role check — a customer can hold the Admin role on their own
  // tenant, so ADMIN/BUILDER is not a staff signal.
  //
  // Status is what the customer reads to know where a request stands, so it has
  // to mean what Pliable says it means. Customers still move it implicitly:
  // replying flips awaiting_pliable / awaiting_customer in the service.
  const isStaff = !!userConfig?.is_internal_staff;

  // Customers get the read-only StatusBadge already rendered in the header.
  if (!isStaff) return null;

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <Select
        value={status}
        onValueChange={(v) =>
          setStatus.mutate({ id: requestId, status: v as RequestStatus })
        }
      >
        <SelectTrigger className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((s) => (
            <SelectItem key={s} value={s}>
              {STATUS_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Thread({
  messages,
  requestId,
  loading,
  onOpenArtifact,
  onOpenThread,
  readBaseline,
}: {
  messages: RequestMessage[];
  requestId: string;
  loading: boolean;
  onOpenArtifact: (artifactId: string, messageId: string) => void;
  onOpenThread: (messageId: string) => void;
  /** Read cursor from before this request was opened; undefined until known. */
  readBaseline: string | null | undefined;
}) {
  const { user } = useAppAuth();
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether the reader is sitting at the bottom of the conversation. Starts
  // true so the first paint lands on the newest message.
  const pinnedRef = useRef(true);
  // The request the scroll position currently belongs to, so switching requests
  // is treated as a fresh open rather than as new messages arriving.
  const scrolledForRef = useRef<string | null>(null);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= PINNED_THRESHOLD_PX;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Opening a request: jump, don't animate. Scrolling the whole history is
    // what made an arriving message look like the page had reloaded.
    if (scrolledForRef.current !== requestId) {
      scrolledForRef.current = requestId;
      pinnedRef.current = true;
      el.scrollTop = el.scrollHeight;
      return;
    }

    // A message arrived. Follow it only if the reader was already at the
    // bottom — someone who has scrolled up is reading, and yanking them to the
    // newest message loses their place mid-sentence.
    if (!pinnedRef.current) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, requestId]);

  // The conversation shows top-level messages only; replies are collapsed behind
  // a reply count, the same way Slack keeps a thread out of the channel. The
  // endpoint returns the flat list, so the split happens here rather than
  // costing a second request.
  const { topLevel, replyCounts } = useMemo(() => {
    const counts = new Map<string, number>();
    const roots: RequestMessage[] = [];
    for (const m of messages) {
      if (m.parent_message_id) {
        counts.set(m.parent_message_id, (counts.get(m.parent_message_id) ?? 0) + 1);
      } else {
        roots.push(m);
      }
    }
    return { topLevel: roots, replyCounts: counts };
  }, [messages]);

  const unreadReplies = useMemo(
    () => unreadReplyCounts(messages, readBaseline, user?.id),
    [messages, readBaseline, user?.id],
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading messages
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="flex-1 overflow-y-auto px-6 py-4 min-w-0"
    >
      <div className={`${CONTENT_WIDTH} space-y-4`}>
        {topLevel.map((msg) => (
          <MessageRow
            key={msg.id}
            message={msg}
            requestId={requestId}
            onOpenArtifact={onOpenArtifact}
            replyCount={replyCounts.get(msg.id) ?? 0}
            unread={isUnread(msg, readBaseline, user?.id)}
            unreadReplies={unreadReplies.get(msg.id) ?? 0}
            // System rows are events, not conversation — nothing to reply to.
            onOpenThread={
              msg.author_type === 'system' ? undefined : () => onOpenThread(msg.id)
            }
          />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}


function AttachmentSidebar({
  requestId,
  attachments,
}: {
  requestId: string;
  attachments: RequestAttachment[];
}) {
  if (attachments.length === 0) return null;

  return (
    <aside className="w-64 border-l border-border flex-shrink-0 overflow-y-auto hidden lg:block">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Files ({attachments.length})
        </h2>
      </div>
      <ul className="p-3 space-y-2">
        {attachments.map((a) => (
          <li key={a.id}>
            <AttachmentChip requestId={requestId} attachment={a} />
          </li>
        ))}
      </ul>
    </aside>
  );
}

type PendingUpload = {
  key: string;
  file: File;
  progress: number;
  attachmentId?: string;
  error?: string;
};

function Composer({ requestId }: { requestId: string }) {
  const [body, setBody] = useState('');
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [dragging, setDragging] = useState(false);
  const [artifactPickerOpen, setArtifactPickerOpen] = useState(false);
  // Artifacts staged for this message. Unlike files there is nothing to upload —
  // they already exist — so they are simply ids carried along on send.
  const [stagedArtifacts, setStagedArtifacts] = useState<EllieArtifact[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const postMessage = usePostMessage(requestId);
  const upload = useUploadAttachment(requestId);

  const uploading = pending.some((p) => !p.attachmentId && !p.error);
  const canSend =
    (body.trim().length > 0 ||
      pending.some((p) => p.attachmentId) ||
      stagedArtifacts.length > 0) &&
    !uploading &&
    !postMessage.isPending;

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      list.forEach((file) => {
        const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
        setPending((prev) => [...prev, { key, file, progress: 0 }]);

        upload
          .mutateAsync({
            file,
            onProgress: (pct) =>
              setPending((prev) =>
                prev.map((p) => (p.key === key ? { ...p, progress: pct } : p)),
              ),
          })
          .then((att) =>
            setPending((prev) =>
              prev.map((p) =>
                p.key === key ? { ...p, attachmentId: att.id, progress: 100 } : p,
              ),
            ),
          )
          .catch((e: Error) =>
            setPending((prev) =>
              prev.map((p) => (p.key === key ? { ...p, error: e.message } : p)),
            ),
          );
      });
    },
    [upload],
  );

  const send = async () => {
    if (!canSend) return;
    const attachmentIds = pending
      .map((p) => p.attachmentId)
      .filter((x): x is string => !!x);
    await postMessage.mutateAsync({
      body: body.trim(),
      attachmentIds,
      artifactIds: stagedArtifacts.map((a) => a.id),
    });
    setBody('');
    setPending([]);
    setStagedArtifacts([]);
  };

  return (
    <div
      className={`border-t border-border px-6 py-3 flex-shrink-0 ${
        dragging ? 'bg-accent/40' : ''
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
      }}
    >
      <div className={CONTENT_WIDTH}>
        {stagedArtifacts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {stagedArtifacts.map((a) => (
              <div
                key={a.id}
                title={a.name}
                className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/40 px-2 py-1 text-xs max-w-[min(20rem,100%)]"
              >
                <FileText className="h-3 w-3 flex-shrink-0" />
                <span className="truncate min-w-0">{a.name}</span>
                <button
                  type="button"
                  onClick={() =>
                    setStagedArtifacts((prev) => prev.filter((x) => x.id !== a.id))
                  }
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                  aria-label={`Remove ${a.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {pending.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {pending.map((p) => (
              <div
                key={p.key}
                title={p.file.name}
                className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs max-w-[min(20rem,100%)] ${
                  p.error
                    ? 'border-destructive/50 text-destructive'
                    : 'border-border bg-muted/40'
                }`}
              >
                {!p.attachmentId && !p.error ? (
                  <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                ) : (
                  <Paperclip className="h-3 w-3 flex-shrink-0" />
                )}
                <span className="truncate min-w-0">{p.file.name}</span>
                {!p.attachmentId && !p.error && (
                  <span className="text-muted-foreground">{p.progress}%</span>
                )}
                {p.error && <span className="truncate min-w-0">— {p.error}</span>}
                <button
                  type="button"
                  onClick={() =>
                    setPending((prev) => prev.filter((x) => x.key !== p.key))
                  }
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                  aria-label={`Remove ${p.file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach files"
            title="Attach files"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={() => setArtifactPickerOpen(true)}
            aria-label="Attach an artifact"
            title="Attach an artifact"
          >
            <FileText className="h-4 w-4" />
          </Button>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter is a newline — the convention everyone
              // already has muscle memory for from Slack.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Write a message, or drop files here"
            rows={2}
            className="resize-none min-h-0"
          />
          <Button
            onClick={send}
            disabled={!canSend}
            className="h-9 flex-shrink-0"
            aria-label="Send message"
          >
            {postMessage.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <AttachArtifactDialog
        open={artifactPickerOpen}
        onOpenChange={setArtifactPickerOpen}
        alreadyAttachedIds={stagedArtifacts.map((a) => a.id)}
        onAttach={(picked) =>
          setStagedArtifacts((prev) => {
            const seen = new Set(prev.map((a) => a.id));
            return [...prev, ...picked.filter((a) => !seen.has(a.id))];
          })
        }
      />
    </div>
  );
}

